import { useState, useCallback } from 'react';
import { json } from '../../../api/apiClient';
import { errorMessage } from '../../../api/errorMessage';

/**
 * Manages label state for the deck editor.
 * Provides optimistic updates for assign/remove and handles full label lifecycle.
 *
 * @param {string} deckId - UI deck ID (e.g. 'virtual_123')
 * @param {Array} initialLabels - Labels from editorSettings (all deck labels)
 * @param {Array} initialCards - Cards from deck response (each has .labels array)
 * @param {Function} [onError] - Zeigt dem Nutzer eine Fehlermeldung (Toast)
 */
export function useDeckLabels(deckId, initialLabels, initialCards, onError) {
  const [deckLabels, setDeckLabels] = useState(initialLabels || []);

  const [cardLabelsMap, setCardLabelsMap] = useState(() => {
    const map = {};
    if (initialCards) {
      for (const card of initialCards) {
        if (card.deckCardEntryId && card.labels) {
          map[card.deckCardEntryId] = card.labels;
        }
      }
    }
    return map;
  });

  const getCardLabels = useCallback((deckCardEntryId) => {
    return cardLabelsMap[deckCardEntryId] || [];
  }, [cardLabelsMap]);

  /**
   * Legt ein Label an und weist es der Karte zu.
   * @returns {Promise<{ok: true, label: Object}|{ok: false, error: string}>} — bei `ok: false`
   *   trägt `error` die Klartext-Meldung des Servers (z.B. das Label-Limit) für einen Toast.
   */
  const createAndAssignLabel = useCallback(async (name, color, deckCardEntryId) => {
    try {
      const label = await json(`/api/decks/${deckId}/labels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, color }),
      });

      setDeckLabels(prev => [...prev, label]);

      await json(`/api/decks/${deckId}/cards/${deckCardEntryId}/labels/${label.id}`, {
        method: 'POST',
      });

      setCardLabelsMap(prev => ({
        ...prev,
        [deckCardEntryId]: [...(prev[deckCardEntryId] || []), label],
      }));

      return { ok: true, label };
    } catch (error) {
      console.error('Fehler beim Erstellen des Labels:', error);
      return { ok: false, error: errorMessage(error, 'Label konnte nicht erstellt werden.') };
    }
  }, [deckId]);

  const assignLabel = useCallback(async (deckCardEntryId, label) => {
    setCardLabelsMap(prev => ({
      ...prev,
      [deckCardEntryId]: [...(prev[deckCardEntryId] || []), label],
    }));

    try {
      await json(`/api/decks/${deckId}/cards/${deckCardEntryId}/labels/${label.id}`, {
        method: 'POST',
      });
    } catch (error) {
      console.error('Fehler beim Zuweisen des Labels:', error);
      setCardLabelsMap(prev => ({
        ...prev,
        [deckCardEntryId]: (prev[deckCardEntryId] || []).filter(l => l.id !== label.id),
      }));
      onError?.(errorMessage(error, 'Label konnte nicht zugewiesen werden.'));
    }
  }, [deckId, onError]);

  const removeLabel = useCallback(async (deckCardEntryId, labelId) => {
    setCardLabelsMap(prev => ({
      ...prev,
      [deckCardEntryId]: (prev[deckCardEntryId] || []).filter(l => l.id !== labelId),
    }));

    try {
      await json(`/api/decks/${deckId}/cards/${deckCardEntryId}/labels/${labelId}`, {
        method: 'DELETE',
      });

      // Check if label is still used by any card — if not, remove from deckLabels
      setCardLabelsMap(currentMap => {
        const anyCardHasLabel = Object.values(currentMap).some(labels =>
          labels.some(l => l.id === labelId)
        );
        if (!anyCardHasLabel) {
          setDeckLabels(prev => prev.filter(l => l.id !== labelId));
        }
        return currentMap;
      });
    } catch (error) {
      console.error('Fehler beim Entfernen des Labels:', error);
      // Die Zuweisung steht serverseitig noch — die optimistische Entfernung zurücknehmen,
      // sonst zeigt die Karte ein Label weniger, als sie hat. Das Label-Objekt kommt aus dem
      // aktuellen deckLabels-State, damit der Callback keine Abhängigkeit darauf braucht.
      setDeckLabels(currentLabels => {
        const restored = currentLabels.find(l => l.id === labelId);
        if (restored) {
          setCardLabelsMap(prev => ({
            ...prev,
            [deckCardEntryId]: (prev[deckCardEntryId] || []).some(l => l.id === labelId)
              ? prev[deckCardEntryId]
              : [...(prev[deckCardEntryId] || []), restored],
          }));
        }
        return currentLabels;
      });
      onError?.(errorMessage(error, 'Label konnte nicht entfernt werden.'));
    }
  }, [deckId, onError]);

  return {
    deckLabels,
    getCardLabels,
    createAndAssignLabel,
    assignLabel,
    removeLabel,
  };
}
